import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { GraduationCap, Eye, EyeOff, AlertCircle, User, Shield } from 'lucide-react';

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register, loading, error, clearError, isAuthenticated } = useAuthStore();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'student' as 'student' | 'curator',
    curatorSecretKey: ''
  });
  const [mbti, setMbti] = useState<{ d1: 'I' | 'E'; d2: 'N' | 'S'; d3: 'T' | 'F'; d4: 'J' | 'P' } | null>({ d1: 'I', d2: 'N', d3: 'T', d4: 'J' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [universities, setUniversities] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedUniversity, setSelectedUniversity] = useState<string>('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const response = await fetch('/api/universities');
        if (response.ok) {
          const data = await response.json();
          setUniversities(data.universities || []);
        } else {
          console.error('Failed to fetch universities - response not OK:', response.status, response.statusText);
          const errorData = await response.json().catch(() => ({}));
          console.error('Error data:', errorData);
        }
      } catch (error) {
        console.error('Failed to fetch universities:', error);
      }
    };
    fetchUniversities();
  }, []);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Enter a valid email';
    }

    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Full name must be at least 2 characters';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (formData.role === 'curator' && !formData.curatorSecretKey.trim()) {
      errors.curatorSecretKey = 'Curator secret key is required for curator accounts';
    }

    if (formData.role === 'student' && !selectedUniversity) {
      errors.university = 'University selection is required for students';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    if (error) {
      clearError();
    }

    if (name === 'role' && value === 'student') {
      setFormData(prev => ({ ...prev, curatorSecretKey: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const registrationData = {
      email: formData.email,
      password: formData.password,
      name: formData.name,
      role: formData.role,
      ...(formData.role === 'curator' && { curatorSecretKey: formData.curatorSecretKey }),
      ...(formData.role === 'student' && mbti ? { mbti: `${mbti.d1}${mbti.d2}${mbti.d3}${mbti.d4}` } : {}),
      ...(formData.role === 'student' && selectedUniversity ? { university: selectedUniversity } : {})
    };

    await register(registrationData).catch(() => undefined);
  };

  return (
    <div className="min-h-[100svh] w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="min-h-[100svh] flex items-center justify-center p-4 sm:p-6 lg:p-8 xl:p-12">
        <div className="w-full max-w-sm sm:max-w-md lg:max-w-lg">
          <div className="text-center mb-6 lg:mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full p-2 mr-3">
                <GraduationCap className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Teaching App
              </h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Create your account to start learning
            </p>
          </div>

          <div className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 dark:border-gray-700/50 p-6 sm:p-8">
            {/* Header */}
            <div className="mb-6 lg:mb-8">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Create Account
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm lg:text-base">
                Join our collaborative learning platform
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-400">
                    {error}
                  </p>
                </div>
              </div>
            )}

            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-5 lg:space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 transition-all duration-200 ${
                    validationErrors.email
                      ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                      : 'border-gray-200 dark:border-gray-600 focus:ring-blue-500/20 focus:border-blue-500'
                  } bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
                  placeholder="Enter your email"
                />
                {validationErrors.email && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {validationErrors.email}
                  </p>
                )}
              </div>

              {/* Full Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 transition-all duration-200 ${
                    validationErrors.name
                      ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                      : 'border-gray-200 dark:border-gray-600 focus:ring-blue-500/20 focus:border-blue-500'
                  } bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
                  placeholder="Enter your full name"
                />
                {validationErrors.name && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {validationErrors.name}
                  </p>
                )}
              </div>

              {/* Role Selection */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Account Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center p-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 transition-colors duration-200">
                    <input
                      type="radio"
                      name="role"
                      value="student"
                      checked={formData.role === 'student'}
                      onChange={handleInputChange}
                      className="mr-3 text-blue-600 focus:ring-4 focus:ring-blue-500/20 h-4 w-4"
                    />
                    <div className="flex items-center">
                      <User className="h-5 w-5 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Student</span>
                    </div>
                  </label>
                  <label className="flex items-center p-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer hover:border-purple-300 dark:hover:border-purple-600 transition-colors duration-200">
                    <input
                      type="radio"
                      name="role"
                      value="curator"
                      checked={formData.role === 'curator'}
                      onChange={handleInputChange}
                      className="mr-3 text-purple-600 focus:ring-4 focus:ring-purple-500/20 h-4 w-4"
                    />
                    <div className="flex items-center">
                      <Shield className="h-5 w-5 text-purple-600 mr-2" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Curator</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Curator Secret Key */}
              {formData.role === 'curator' && (
                <div>
                  <label htmlFor="curatorSecretKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Curator Secret Key
                  </label>
                  <input
                    type="password"
                    id="curatorSecretKey"
                    name="curatorSecretKey"
                    value={formData.curatorSecretKey}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 transition-all duration-200 ${
                      validationErrors.curatorSecretKey
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                        : 'border-gray-200 dark:border-gray-600 focus:ring-purple-500/20 focus:border-purple-500'
                    } bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
                    placeholder="Enter curator secret key"
                  />
                  {validationErrors.curatorSecretKey && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      {validationErrors.curatorSecretKey}
                    </p>
                  )}
                </div>
              )}

              {/* University Selection - Students only */}
              {formData.role === 'student' && (
                <div>
                  <label htmlFor="university" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    University
                  </label>
                  <select
                    id="university"
                    value={selectedUniversity}
                    onChange={(e) => {
                      setSelectedUniversity(e.target.value);
                      if (validationErrors.university) {
                        setValidationErrors(prev => ({ ...prev, university: '' }));
                      }
                    }}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-4 transition-all duration-200 ${
                      validationErrors.university
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                        : 'border-gray-200 dark:border-gray-600 focus:ring-blue-500/20 focus:border-blue-500'
                    } bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white`}
                  >
                    <option value="">Select your university</option>
                    {universities.map((uni) => (
                      <option key={uni.id} value={uni.name}>
                        {uni.name}
                      </option>
                    ))}
                  </select>
                  {validationErrors.university && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      {validationErrors.university}
                    </p>
                  )}
                </div>
              )}

              {/* MBTI Selector - Students only */}
              {formData.role === 'student' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Personality Preference (MBTI)
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">Select one letter from each column.</p>
                  <div className="grid grid-cols-4 gap-2">
                    {/* Column 1: I / E */}
                    <div className="flex flex-col space-y-2">
                      <button
                        type="button"
                        onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'T', d4: 'J' }), d1: 'I' }))}
                        className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d1 === 'I' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                      >I</button>
                      <button
                        type="button"
                        onClick={() => setMbti(prev => ({ ...(prev || { d1: 'E', d2: 'N', d3: 'T', d4: 'J' }), d1: 'E' }))}
                        className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d1 === 'E' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                      >E</button>
                    </div>
                    {/* Column 2: N / S */}
                    <div className="flex flex-col space-y-2">
                      <button
                        type="button"
                        onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'T', d4: 'J' }), d2: 'N' }))}
                        className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d2 === 'N' ? 'bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                      >N</button>
                      <button
                        type="button"
                        onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'S', d3: 'T', d4: 'J' }), d2: 'S' }))}
                        className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d2 === 'S' ? 'bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                      >S</button>
                    </div>
                    {/* Column 3: T / F */}
                    <div className="flex flex-col space-y-2">
                      <button
                        type="button"
                        onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'T', d4: 'J' }), d3: 'T' }))}
                        className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d3 === 'T' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 text-purple-700 dark:text-purple-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                      >T</button>
                      <button
                        type="button"
                        onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'F', d4: 'J' }), d3: 'F' }))}
                        className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d3 === 'F' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 text-purple-700 dark:text-purple-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                      >F</button>
                    </div>
                    {/* Column 4: J / P */}
                    <div className="flex flex-col space-y-2">
                      <button
                        type="button"
                        onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'T', d4: 'J' }), d4: 'J' }))}
                        className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d4 === 'J' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 text-amber-700 dark:text-amber-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                      >J</button>
                      <button
                        type="button"
                        onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'T', d4: 'P' }), d4: 'P' }))}
                        className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d4 === 'P' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 text-amber-700 dark:text-amber-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                      >P</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 pr-12 border-2 rounded-xl focus:outline-none focus:ring-4 transition-all duration-200 ${
                      validationErrors.password
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                        : 'border-gray-200 dark:border-gray-600 focus:ring-blue-500/20 focus:border-blue-500'
                    } bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {validationErrors.password && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {validationErrors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 pr-12 border-2 rounded-xl focus:outline-none focus:ring-4 transition-all duration-200 ${
                      validationErrors.confirmPassword
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500'
                        : 'border-gray-200 dark:border-gray-600 focus:ring-blue-500/20 focus:border-blue-500'
                    } bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {validationErrors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {validationErrors.confirmPassword}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-indigo-400 disabled:to-purple-400 text-white font-semibold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Creating account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-6 lg:mt-8 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors duration-200"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
